import {
  ExecutionPatchIncrementalResult,
  ExecutionPatchInitialResult,
  SingleExecutionResult,
} from '@apollo/client';
import { formatInTimeZone } from 'date-fns-tz';

import { GetArticleIdQuery, GetAssemblyCollectionPageIdQuery } from '../graphql/__generated/sdk';

const REGEX_CONTENTFUL_ASSETS = /(images|videos|downloads|assets)\.ctfassets\.net/g;

export const SCROLL_STOP = 'scroll-stop';

export function isMobileView(): boolean {
  const element = document.querySelectorAll('[data-mobile-detector]')[0];
  return !!(element && getComputedStyle(element).display !== 'none');
}

export default function replaceContentfulAssetsHost(
  response: SingleExecutionResult | ExecutionPatchInitialResult | ExecutionPatchIncrementalResult
) {
  return JSON.parse(
    JSON.stringify(response).replaceAll(REGEX_CONTENTFUL_ASSETS, `${process.env.REACT_APP_HOST}/$1`)
  );
}

export function disableScroll() {
  document.body.classList.add(SCROLL_STOP);
}

export function enableScroll() {
  document.body.classList.remove(SCROLL_STOP);
}

export function generateArrayWithUniqueEntries<T>(list: T[]): T[] {
  return Array.from(new Set(list));
}

export function formatDateToUTC(date: string, dateFormat?: string): string {
  return formatInTimeZone(new Date(date), 'UTC', dateFormat || 'yyyy-MM-dd');
}

export function getSlugFromLocationPath(pathname: string): string | undefined {
  return pathname.split('/').pop();
}

export function getEntryId(data: GetArticleIdQuery | GetAssemblyCollectionPageIdQuery | undefined) {
  if (data?.svData?.items.length) {
    return data?.svData?.items[0].sys.id;
  }

  if (data?.enData?.items.length) {
    return data?.enData?.items[0].sys.id;
  }
}

function getAspectRatioWidth(aspectRatio: `${string}:${string}`, height: number): number {
  const [widthAspect, heightAspect] = aspectRatio.split(':');
  return Math.ceil((height * Number(widthAspect)) / Number(heightAspect));
}

// Tuple values: [Aspect-Ratio, Height]
export type Dimensions = {
  small: [`${string}:${string}`, number];
  medium: [`${string}:${string}`, number];
  large: [`${string}:${string}`, number];
};

const defaultDimensions: Dimensions = {
  small: ['4:3', 450],
  medium: ['16:9', 650],
  large: ['16:9', 1125],
};

export function createMultipleSizeImages(
  image: {
    url: string;
    altText?: string;
    focusArea?: string;
  },
  dimensions = defaultDimensions
) {
  const FOCUS_AREA = image.focusArea ? image.focusArea : 'center';
  const RESIZE_STRATEGY = 'fill';
  const sizesInPixels = {
    small: [getAspectRatioWidth(...dimensions.small), dimensions.small[1]],
    medium: [getAspectRatioWidth(...dimensions.medium), dimensions.medium[1]],
    large: [getAspectRatioWidth(...dimensions.large), dimensions.large[1]],
  };

  return {
    default: image.url,
    small: `${image.url}&f=${FOCUS_AREA}&fit=${RESIZE_STRATEGY}&w=${sizesInPixels.small[0]}&h=${sizesInPixels.small[1]}`,
    medium: `${image.url}&f=${FOCUS_AREA}&fit=${RESIZE_STRATEGY}&w=${sizesInPixels.medium[0]}&h=${sizesInPixels.medium[1]}`,
    large: `${image.url}&f=${FOCUS_AREA}&fit=${RESIZE_STRATEGY}&w=${sizesInPixels.large[0]}&h=${sizesInPixels.large[1]}`,
    altText: image.altText || '',
  };
}

export function parseNumericString(val: any) {
  return !isNaN(val) ? parseInt(val) : val;
}

export function capitalizeText(val: string) {
  return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
}
